/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useContext, useState } from "react";
import { ThemeContext } from "../../App";

import { Box, AppBar, Toolbar, CssBaseline, Typography, Button, Menu, MenuProps, MenuItem } from "@mui/material";

import { Brightness3, Brightness7 } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import GridViewIcon from "@mui/icons-material/GridView";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";

import { selectFullName, selectLastName } from "../auth/authSlice";
import { logoutHandler } from "../auth/authFunctions";

import { styled, alpha } from "@mui/material/styles";

import { useWindowDimensions } from "../../utils/Hooks";
import { Effect, Nullable } from "../../utils/Interfaces";
import NotificationsBell from "./NotificationsBell";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

const StyledMenu = styled((props: MenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
        }}
        transformOrigin={{
            vertical: "top",
            horizontal: "right",
        }}
        {...props}
    />
))(({ theme }) => ({
    "& .MuiPaper-root": {
        borderRadius: 0,
        marginTop: theme.spacing(1),
        color: "red",
        minWidth: 240,
        "& .MuiMenuItem-root": {
            "& .MuiSvgIcon-root": {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            "&:active": {
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
            },
        },
    },
}));

export interface NavProps {
    children: JSX.Element | JSX.Element[];
    forceRender: Effect;
}

const BorderlessNav = (props: NavProps) => {
    const { children, forceRender } = props;

    const isDarkTheme = localStorage.getItem("techwiseTheme") === "dark";
    const useBorderlessTheme: boolean = localStorage.getItem("navTheme") === "borderless";

    const { width } = useWindowDimensions();
    const isMobile = width <= 1200;

    const { setTheme } = useContext(ThemeContext);

    const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<Nullable<HTMLElement>>(null);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setUserMenuOpen(true);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setUserMenuOpen(false);
    };

    const dispatch = useAppDispatch();

    const fullName: string = useAppSelector(selectFullName);
    const lastName: string = useAppSelector(selectLastName);

    const toggleDarkTheme = () => {
        const theme: "dark" | "light" = isDarkTheme ? "light" : "dark";
        setTheme(theme);
        localStorage.setItem("techwiseTheme", theme);
    };

    const toggleNavTheme = () => {
        const navTheme: "classic" | "borderless" = useBorderlessTheme ? "classic" : "borderless";
        localStorage.setItem("navTheme", navTheme);
        forceRender();
    };

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    background: isDarkTheme
                        ? (process.env.REACT_APP_BACKGROUND_COLOR_DARK as string)
                        : (process.env.REACT_APP_PRIMARY_COLOR_LIGHT as string),
                }}
            >
                <Toolbar>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {isMobile ? process.env.REACT_APP_TITLE_MOBILE : process.env.REACT_APP_TITLE_DESKTOP}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <NotificationsBell />
                        <Button
                            variant="text"
                            id="demo-customized-button"
                            aria-controls={userMenuOpen ? "demo-customized-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={userMenuOpen ? "true" : undefined}
                            disableElevation
                            onClick={handleClick}
                            endIcon={<KeyboardArrowDownIcon />}
                            color={"inherit"}
                        >
                            <Typography color="inherit" fontSize={20} marginRight={1}>
                                {isMobile ? lastName : fullName}
                            </Typography>
                        </Button>
                        <StyledMenu
                            id="demo-customized-menu"
                            MenuListProps={{
                                "aria-labelledby": "demo-customized-button",
                            }}
                            anchorEl={anchorEl}
                            open={userMenuOpen}
                            onClose={handleClose}
                        >
                            <MenuItem
                                onClick={() => {
                                    toggleDarkTheme();
                                    handleClose();
                                }}
                                disableRipple
                                sx={{ color: isDarkTheme ? "white" : "black" }}
                            >
                                {isDarkTheme ? <Brightness7 /> : <Brightness3 />}
                                {isDarkTheme ? "Enable Light Theme" : "Enable Dark Theme"}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    toggleNavTheme();
                                    handleClose();
                                }}
                                disableRipple
                                sx={{ color: isDarkTheme ? "white" : "black" }}
                            >
                                {useBorderlessTheme ? <ViewSidebarIcon /> : <GridViewIcon />}
                                {useBorderlessTheme ? "Enable Classic Navigation" : "Enable Tile Navigation"}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    logoutHandler(dispatch);
                                    handleClose();
                                }}
                                sx={{ color: isDarkTheme ? "white" : "black" }}
                                disableRipple
                            >
                                <LogoutIcon />
                                Logout
                            </MenuItem>
                        </StyledMenu>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box component="main" sx={{ flexGrow: 1, p: 2 }}>
                <Toolbar />
                {children}
            </Box>
        </Box>
    );
};

export default BorderlessNav;
